"use client";

import React from "react";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useModal } from "@getpara/react-sdk";
import config from "@/config";
import Link from "next/link";
import { useTokenStore } from "@/lib/zustand";
import { getToken, ChainType, getChains } from "@lifi/sdk";
import { BadgeDollarSign, MenuIcon, History, ExternalLink, Mic2, Flame } from "lucide-react";
import { RecycleIcon, CoinsIcon } from "lucide-react";
import { useState } from "react";
import { HistoryModal } from "../wallet/history-modal";
import {
  RiDiscordFill,
  RiTwitterXFill,
  RiTelegramFill,
} from "@remixicon/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "../ui/dropdown-menu";
import { UserSettingsModal } from "../modals/user-settings-modal";
import { ConnectButton } from "../wallet/para-connect-button";
import { WalletNetworkSwitcher } from "../wallet/wallet-network-switcher";

import { MobilePortfolioToggle } from "../wallet/mobile-portfolio-toggle";

type NavigationItem = {
  label: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: () => void;
};

const Navigation = () => {
  const { setToChain, setToToken } = useTokenStore();
  const { openModal } = useModal();
  const router = useRouter();
  const pathname = usePathname();
  const isWhitelabel = pathname === "/whitelabel";
  const isWidget = pathname.startsWith("/widget/");
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const navigationItems: Record<string, { label: string; items: NavigationItem[] }> = {
    products: {
      label: "Products",
      items: [
        {
          label: "Swap",
          icon: <RecycleIcon className="size-4" />,
          href: "/",
        },
        {
          label: "Burn",
          icon: <Flame className="size-4" />,
          href: "/burn",
        },
        {
          label: "Convert Crypto to Cash",
          icon: <BadgeDollarSign className="size-4" />,
          onClick: () => openModal({ step: 'ACCOUNT_ADD_FUNDS_WITHDRAW' }),
        },
        {
          label: "History",
          icon: <History className="size-4" />,
          onClick: () => setIsHistoryOpen(true),
        },
        // TODO: Re-enable once whitelabel feature is fully ready (admin auth, third-party allowlists, fee attribution)
        // {
        //   label: "Get a Branded Swap for Your Token",
        //   icon: <CoinsIcon className="size-4" />,
        //   href: "/whitelabel",
        // },
        {
          label: "Get Your Token Featured",
          icon: <CoinsIcon className="size-4" />,
          href: "/featured",
        },
        {
          label: "Advertise With Us",
          icon: <BadgeDollarSign className="size-4" />,
          href: "/advertise",
        },
        {
          label: "Become an Affiliate",
          icon: <BadgeDollarSign className="size-4" />,
          href: "/affiliates",
        },
        {
          label: "X Spaces",
          icon: <Mic2 className="size-4" />,
          href: "/spaces",
        },
        {
          label: "FAQ",
          icon: <BadgeDollarSign className="size-4" />,
          href: "/faq",
        },
        {
          label: "Forge Launchpad",
          icon: <CoinsIcon className="size-4" />,
          href: "https://forgepad.fun",
        },
      ],
    },
    // leaderboard: {
    //   label: "Leaderboard",
    //   items: [
    //     {
    //       label: "Volume",
    //       icon: <LineChartIcon className="size-4" />,
    //       href: "/volume-leaderboard",
    //     },
    //     {
    //       label: "Referrals",
    //       icon: <BarChartBigIcon className="size-4" />,
    //       href: "/referral-leaderboard",
    //     },
    //   ],
    // },
    socials: {
      label: "Socials",
      items: [
        {
          label: "X (Twitter)",
          icon: <RiTwitterXFill className="size-4" />,
          href: config.socials.twitter,
        },
        {
          label: "Telegram",
          icon: <RiTelegramFill className="size-4" />,
          href: config.socials.telegram,
        },
        // Discord hidden until server is ready
        // {
        //   label: "Discord",
        //   icon: <RiDiscordFill className="size-4" />,
        //   href: config.socials.discord,
        // },
      ],
    },
  };

  if (isWidget) return null;

  // Removed Prevail Token shortcut; linking directly to ForgeSwap website instead

  // debugging
  // useEffect(() => {
  //   console.log("current to token", toToken);
  //   console.log("current to chain", toChain);
  // }, [toToken, toChain]);

  return (
    <div className="relative">
      <nav className="w-full h-[80px] px-8 py-4 flex flex-col gap-4 items-center bg-[rgba(9,9,11,0.85)] backdrop-blur-[20px] border-b border-[rgba(255,255,255,0.04)]">
        <div className="w-full flex justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <Link href="/">
              <Image
                src={config.logoPath}
                alt="logo"
                width={150}
                height={50}
                priority
              />
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <MenuIcon className="size-6 text-[#666] hover:text-[#999] transition-colors duration-200 cursor-pointer" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" side="bottom">
                {Object.entries(navigationItems).map(([key, section]) => (
                  <React.Fragment key={key}>
                    <DropdownMenuLabel className="text-xs text-white">
                      {section.label}
                    </DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {section.items.map((item) => (
                        <DropdownMenuItem
                          key={item.label}
                          className="text-white"
                        >
                          {item.onClick ? (
                            <button
                              type="button"
                              onClick={item.onClick}
                              className="flex w-full items-center"
                            >
                              {item.icon}
                              <span className="ml-2">{item.label}</span>
                            </button>
                          ) : (
                            <Link
                              href={item.href ?? ""}
                              className="flex items-center w-full"
                              target={item.href?.startsWith("http") ? "_blank" : undefined}
                              rel={item.href?.startsWith("http") ? "noopener noreferrer" : undefined}
                            >
                              {item.icon}
                              <span className="ml-2">{item.label}</span>
                              {item.href?.startsWith("http") && (
                                <ExternalLink className="size-3 ml-auto text-muted-foreground" />
                              )}
                            </Link>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuGroup>
                  </React.Fragment>
                ))}
                <div className="flex flex-col items-center justify-center mt-8">
                  <p className="text-xs text-white text-center">
                    &copy; {new Date().getFullYear()} {config.companyName}{" "}
                    Token, All Rights Reserved
                  </p>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
                    <div className="flex items-center gap-4">
            <div className="lg:hidden">
              <UserSettingsModal />
            </div>
            {/* <appkit-button /> */}
            <div className="hidden sm:block">
              <WalletNetworkSwitcher />
            </div>
            <div className="hidden sm:block">
              <ConnectButton />
            </div>
          </div>
        </div>
      </nav>
      
      {/* Mobile Connect & Switcher - Sticky Bottom */}
      {!isWhitelabel && (
        <div className="sm:hidden fixed bottom-0 left-0 w-full max-w-[100vw] overflow-hidden bg-background/95 backdrop-blur-sm border-t border-border z-50 px-3 py-3 pb-5 flex items-center justify-between gap-2">
          <div className="flex-none">
            <MobilePortfolioToggle />
          </div>
          <div className="flex-1 min-w-0 flex gap-1.5 items-center overflow-hidden">
            <div className="flex-none">
              <WalletNetworkSwitcher />
            </div>
            <div className="flex-1 min-w-0">
              <ConnectButton />
            </div>
          </div>
        </div>
      )}

      <HistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      />
    </div>
  );
};

export default Navigation;
